import { useEffect, useRef, useState } from 'react';
import { TextField } from '../primitives';
import { PropertyPill } from '../primitives/PropertyPill';
import { resolveScalarDisplay } from '../primitives/propertyBarDisplay';
import type { Database } from '../../types/database';

type WorkItemPropertyDefRow =
  Database['public']['Tables']['work_item_property_defs']['Row'];
type WorkItemPropertyValueRow =
  Database['public']['Tables']['work_item_property_values']['Row'];

const COMMIT_DEBOUNCE_MS = 600;

export interface WorkItemCanvasCustomPillProps {
  def: WorkItemPropertyDefRow;
  value: WorkItemPropertyValueRow | null;
  editable: boolean;
  testIdPrefix: string;
  onCommit: (defId: string, value: unknown) => Promise<void>;
}

function readScalar(v: WorkItemPropertyValueRow['value'] | undefined): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  return '';
}

export function WorkItemCanvasCustomPill({
  def,
  value,
  editable,
  testIdPrefix,
  onCommit
}: WorkItemCanvasCustomPillProps) {
  const persisted = readScalar(value?.value);
  const [draft, setDraft] = useState(persisted);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftRef = useRef(persisted);

  useEffect(() => {
    if (debounceRef.current) return;
    if (draftRef.current === persisted) return;
    setDraft(persisted);
    draftRef.current = persisted;
  }, [persisted]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const coerce = (raw: string): unknown => {
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    if (def.kind === 'number') {
      const n = Number(trimmed);
      return Number.isFinite(n) ? n : null;
    }
    return trimmed;
  };

  const flush = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const next = coerce(draftRef.current);
    const current = value?.value ?? null;
    if (next === current) return;
    void onCommit(def.id, next);
  };

  const schedule = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      flush();
    }, COMMIT_DEBOUNCE_MS);
  };

  const displayValue = resolveScalarDisplay(
    { kind: def.kind === 'number' ? 'number' : 'text', label: def.label },
    value?.value
  );
  const inputType = def.kind === 'number' ? 'number' : 'text';
  const inputId = `work-item-canvas-bar-${def.key}-input`;
  const pillTestId = `${testIdPrefix}-pill-${def.key}`;

  return (
    <PropertyPill
      label={def.label}
      displayValue={displayValue}
      editable={editable}
      testId={pillTestId}
    >
      {() => (
        <TextField
          id={inputId}
          label={def.label}
          hideLabel
          type={inputType}
          value={draft}
          onChange={(e) => {
            const next = e.target.value;
            setDraft(next);
            draftRef.current = next;
            schedule();
          }}
          onBlur={() => {
            if (debounceRef.current) {
              clearTimeout(debounceRef.current);
              debounceRef.current = null;
              flush();
            }
          }}
          data-testid={`work-item-canvas-bar-${def.key}-input`}
        />
      )}
    </PropertyPill>
  );
}
